import { msg } from "@lingui/core/macro";
import { createEffect, type JSX, Show, ErrorBoundary as SolidErrorBoundary } from "solid-js";
import { AlertTriangle, RefreshCw, RotateCcw } from "@fleet/icons";
import { cn } from "@tendly/shared";
import { Button } from "@/components/ui/button";
import { GlassSurface } from "@fleet/ui";
import { useI18n } from "@/providers/i18n-provider";

/**
 * Theme-aware illustration (uses `currentColor`) for the error fallback.
 * Decorative: the title/description carry the semantics.
 */
function ErrorIllustration() {
  return (
    <svg
      viewBox="0 0 160 120"
      fill="none"
      aria-hidden="true"
      class="w-40 max-w-full text-destructive/70"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Suelo */}
      <line
        x1="24"
        y1="100"
        x2="136"
        y2="100"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        opacity="0.25"
      />
      {/* Broken gear / warning triangle */}
      <path d="M80 28 116 92H44L80 28Z" fill="currentColor" opacity="0.1" />
      <path
        d="M80 28 116 92H44L80 28Z"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linejoin="round"
      />
      <path d="M80 54v20" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
      <circle cx="80" cy="84" r="2.6" fill="currentColor" />
      {/* Destellos */}
      <circle cx="34" cy="40" r="2.5" fill="currentColor" opacity="0.35" />
      <circle cx="128" cy="48" r="2" fill="currentColor" opacity="0.3" />
    </svg>
  );
}

interface AppErrorBoundaryProps {
  children: JSX.Element;
  /** Compact variant for embedded regions (not full screen). */
  region?: boolean;
  /** Region label for the log (e.g. "dashboard", "asset-detail"). */
  label?: string;
  class?: string;
}

interface ErrorFallbackProps {
  error: unknown;
  reset: () => void;
  region?: boolean;
  label?: string;
  class?: string;
}

/** Premium theme-aware fallback. Top-level component (not nested in render). */
function ErrorFallback(props: ErrorFallbackProps): JSX.Element {
  const { t } = useI18n();

  createEffect(() => {
    console.error(`[ErrorBoundary${props.label ? `:${props.label}` : ""}]`, props.error);
  });

  const message = () =>
    props.error instanceof Error ? props.error.message : t(msg`An unexpected error has occurred.`);

  return (
    <div
      role="alert"
      class={cn(
        "flex w-full items-center justify-center p-4",
        props.region ? "min-h-[40vh]" : "min-h-dvh bg-background",
        props.class,
      )}
    >
      <GlassSurface class="flex w-full max-w-md flex-col items-center gap-6 p-8 text-center animate-scale-in">
        <div class="animate-float">
          <Show
            when={!props.region}
            fallback={<AlertTriangle class="h-12 w-12 text-destructive/70" />}
          >
            <ErrorIllustration />
          </Show>
        </div>
        <div class="space-y-2">
          <h2 class="text-xl font-bold tracking-tight">
            {props.region ? t(msg`This section ran into a problem`) : t(msg`Something went wrong`)}
          </h2>
          <p class="mx-auto max-w-sm text-sm text-muted-foreground">
            {props.region
              ? t(msg`Try again — the rest of the app keeps working.`)
              : t(msg`Don't worry, your data is safe on your device.`)}
          </p>
          <Show when={message()}>
            <p class="mx-auto max-w-sm break-words rounded-md bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground/80">
              {message()}
            </p>
          </Show>
        </div>
        <div class="flex w-full gap-3">
          <Button
            variant="outline"
            class="flex-1 gap-2"
            onClick={() => {
              globalThis.location.reload();
            }}
          >
            <RotateCcw class="h-4 w-4" />
            {t(msg`Reload`)}
          </Button>
          <Button
            class="flex-1 gap-2"
            onClick={() => {
              props.reset();
            }}
          >
            <RefreshCw class="h-4 w-4" />
            {t(msg`Retry`)}
          </Button>
        </div>
      </GlassSurface>
    </div>
  );
}

/**
 * Global and region ErrorBoundary. Never leaves a blank screen: shows a
 * premium theme-aware fallback with "Retry" (resets the boundary) and
 * "Reload" (reloads the page). Logs the error with its region label.
 */
export function AppErrorBoundary(props: AppErrorBoundaryProps): JSX.Element {
  // Render-prop required by Solid's ErrorBoundary; the real content lives in
  // the top-level <ErrorFallback> component, so it is not a nested component.
  const renderFallback = (error: unknown, reset: () => void): JSX.Element => (
    <ErrorFallback
      error={error}
      reset={reset}
      region={props.region}
      label={props.label}
      class={props.class}
    />
  );

  return <SolidErrorBoundary fallback={renderFallback}>{props.children}</SolidErrorBoundary>;
}
